import { useRef, useCallback, useEffect, useMemo, useState } from "react";
import type { TimelineProject, TimelineAction } from "@scroll/shared-types/timeline";
import PlaybackControls from "./PlaybackControls";
import TimelineRuler from "./TimelineRuler";
import VideoTrack from "./VideoTrack";
import TextOverlayTrack from "./TextOverlayTrack";
import AudioTrackRow from "./AudioTrackRow";
import Playhead from "./Playhead";
import type { TimelineCapabilities } from "@/lib/timeline-capabilities";

interface TimelinePanelProps {
  project: TimelineProject;
  currentTime: number;
  totalDuration: number;
  isPlaying: boolean;
  selectedClipId: string | null;
  selectedOverlayId: string | null;
  onTogglePlayback: () => void;
  onSeekTo: (time: number) => void;
  onSelectClip: (id: string | null) => void;
  onSelectOverlay: (id: string | null) => void;
  onDispatch: (action: TimelineAction) => void;
  onPause: () => void;
  onAddAudio: () => void;
  capabilities: TimelineCapabilities;
}

const BASE_PPS = 50; // pixels per second at 100%
const ZOOM_PRESETS = [0.5, 0.75, 1, 1.5, 2, 3, 4] as const;
const DEFAULT_ZOOM_INDEX = 2;
const TRACK_HORIZONTAL_PADDING = 96;

export default function TimelinePanel({
  project,
  currentTime,
  totalDuration,
  isPlaying,
  selectedClipId,
  selectedOverlayId,
  onTogglePlayback,
  onSeekTo,
  onSelectClip,
  onSelectOverlay,
  onDispatch,
  onPause,
  onAddAudio,
  capabilities,
}: TimelinePanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [timelineFitToWidth, setTimelineFitToWidth] = useState(true);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateViewportWidth = () => setViewportWidth(el.clientWidth);
    updateViewportWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateViewportWidth);
      return () => window.removeEventListener("resize", updateViewportWidth);
    }

    const observer = new ResizeObserver(updateViewportWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fitZoom = useMemo(() => {
    if (totalDuration <= 0 || viewportWidth <= TRACK_HORIZONTAL_PADDING) return 1;
    const fitPps = (viewportWidth - TRACK_HORIZONTAL_PADDING) / totalDuration;
    return Math.max(0.25, Math.min(4, fitPps / BASE_PPS));
  }, [totalDuration, viewportWidth]);

  const zoom = timelineFitToWidth ? fitZoom : ZOOM_PRESETS[zoomIndex];
  const pixelsPerSecond = totalDuration > 0 ? BASE_PPS * zoom : 100;
  const minimumTrackWidth = Math.max(0, viewportWidth - TRACK_HORIZONTAL_PADDING);
  const trackWidth = Math.max(totalDuration * pixelsPerSecond, minimumTrackWidth);
  const zoomLabel = timelineFitToWidth
    ? "Fit"
    : `${Math.round(ZOOM_PRESETS[zoomIndex] * 100)}%`;

  const handleZoomOut = useCallback(() => {
    setTimelineFitToWidth(false);
    setZoomIndex((index) => {
      if (!timelineFitToWidth) return Math.max(0, index - 1);
      const nextFitIndex = [...ZOOM_PRESETS]
        .map((preset, presetIndex) => ({ preset, presetIndex }))
        .filter(({ preset }) => preset < fitZoom)
        .at(-1)?.presetIndex;
      return nextFitIndex ?? 0;
    });
  }, [fitZoom, timelineFitToWidth]);

  const handleZoomIn = useCallback(() => {
    setTimelineFitToWidth(false);
    setZoomIndex((index) => {
      if (!timelineFitToWidth) return Math.min(ZOOM_PRESETS.length - 1, index + 1);
      const nextFitIndex = ZOOM_PRESETS.findIndex((preset) => preset > fitZoom);
      return nextFitIndex === -1 ? ZOOM_PRESETS.length - 1 : nextFitIndex;
    });
  }, [fitZoom, timelineFitToWidth]);

  const handleFitToWidth = useCallback(() => {
    setTimelineFitToWidth(true);
    scrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  }, []);

  const handleToggleCollapsed = useCallback(() => {
    setIsCollapsed((value) => !value);
  }, []);

  const handleScrubStart = useCallback(() => {
    if (isPlaying) onPause();
  }, [isPlaying, onPause]);

  // Handle click on empty area to seek
  const handleTrackAreaClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only handle direct clicks on the tracks container, not bubbled from children
      if (e.target !== e.currentTarget) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left + (scrollRef.current?.scrollLeft || 0);
      const time = Math.max(0, Math.min(x / pixelsPerSecond, totalDuration));
      onSeekTo(time);
    },
    [pixelsPerSecond, totalDuration, onSeekTo],
  );

  return (
    <div className={`timeline-panel ${isCollapsed ? "timeline-panel-collapsed" : ""}`}>
      <PlaybackControls
        isPlaying={isPlaying}
        currentTime={currentTime}
        totalDuration={totalDuration}
        isCollapsed={isCollapsed}
        zoomLabel={zoomLabel}
        canZoomOut={
          timelineFitToWidth ? fitZoom > ZOOM_PRESETS[0] : zoomIndex > 0
        }
        canZoomIn={
          timelineFitToWidth
            ? fitZoom < ZOOM_PRESETS[ZOOM_PRESETS.length - 1]
            : zoomIndex < ZOOM_PRESETS.length - 1
        }
        onTogglePlayback={onTogglePlayback}
        onSeekTo={onSeekTo}
        onZoomOut={handleZoomOut}
        onZoomIn={handleZoomIn}
        onFitToWidth={handleFitToWidth}
        onToggleCollapsed={handleToggleCollapsed}
      />

      <div className="timeline-tracks" ref={scrollRef} hidden={isCollapsed}>
        <div
          className="timeline-tracks-inner"
          style={{ width: `${trackWidth + TRACK_HORIZONTAL_PADDING}px` }}
          onClick={handleTrackAreaClick}
        >
          <TimelineRuler
            totalDuration={totalDuration}
            pixelsPerSecond={pixelsPerSecond}
          />

          <VideoTrack
            project={project}
            pixelsPerSecond={pixelsPerSecond}
            selectedClipId={selectedClipId}
            onSelectClip={onSelectClip}
            onDispatch={onDispatch}
            canAddClips={capabilities.supportsMultiClip && project.clips.length < capabilities.maxClips}
            canReorderClips={capabilities.supportsMultiClip}
            canEditTransitions={capabilities.supportsTransitions}
          />

          <TextOverlayTrack
            project={project}
            pixelsPerSecond={pixelsPerSecond}
            selectedOverlayId={selectedOverlayId}
            onSelectOverlay={onSelectOverlay}
            onDispatch={onDispatch}
          />

          <AudioTrackRow
            project={project}
            pixelsPerSecond={pixelsPerSecond}
            onDispatch={onDispatch}
            onAddAudio={onAddAudio}
          />

          <Playhead
            currentTime={currentTime}
            totalDuration={totalDuration}
            pixelsPerSecond={pixelsPerSecond}
            onSeek={onSeekTo}
            onScrubStart={handleScrubStart}
          />
        </div>
      </div>
    </div>
  );
}
